import {
    checkFrenchText,
    checkSpanishText,
    checkGermanText,
} from 'bad-words-checker';

import {
    RegExpMatcher,
    englishDataset,
    englishRecommendedTransformers
} from 'obscenity';//https://www.npmjs.com/package/obscenity

// Define custom banned words per language for more specific filtering.
// This allows you to add context-specific profanity for each language.
const customWordsByLanguage: Record<string, string[]> = {
    english: ['foobar', 'dummyhead', 'numbnuts'],
    spanish: ['tonto', 'estupido'], // Example Spanish words
    // Add other languages as needed
};

// English matcher setup from 'obscenity' library.
const englishMatcher = new RegExpMatcher({
    ...englishDataset.build(),
    ...englishRecommendedTransformers
});

/**
 * Checks if the given text contains any profanity for the specified language.
 *
 * @param language The language of the text (e.g., 'english', 'hindi').
 * @param text The text to check for profanity.
 * @returns A boolean indicating whether profanity was found.
 */
const checkText = (language: string, text: string): boolean => {
    const lowerText = text.toLowerCase();
    const normalizedLanguage = language.toLowerCase();

    // Check for custom words specific to the language.
    const hasCustomProfanity = (customWordsByLanguage[normalizedLanguage] || []).some(word =>
        lowerText.includes(word.toLowerCase())
    );

    // Check for library-specific profanity using a switch statement.
    const hasLibraryProfanity = (() => {
        switch (normalizedLanguage) {
            case 'english':
                return englishMatcher.hasMatch(text);
            case 'french':
                return checkFrenchText(text).length > 0;
            case 'spanish':
                return checkSpanishText(text).length > 0;
            case 'german':
                return checkGermanText(text).length > 0;
            default:
                // Default to English if the language is not recognized.
                return englishMatcher.hasMatch(text);
        }
    })();

    return hasCustomProfanity || hasLibraryProfanity;
};

/**
 * A validator function for forms that checks for profanity in a text input.
 * This is designed to be easily integrated into form libraries like Ant Design,
 * Material-UI, or any custom form validation system that uses a Promise-based
 * validation pattern. It checks for both predefined custom words and
 * language-specific profanity using external libraries.
 *
 * @param language The language context for the validation, e.g., 'english', 'french', etc.
 * This determines which language-specific profanity check to run.
 * @param message The custom error message to be returned if validation fails.
 * Defaults to 'Please remove inappropriate language'.
 * @returns A function that takes form validation parameters and returns a Promise.
 * The Promise resolves if the input is valid (no profanity) and rejects
 * with an Error object if profanity is detected.
 */
export const multilingualProfanityValidator = (
    language: string,
    message = 'Please remove inappropriate language'
) => {
    return (_: any, value: string) => {
        // Only validate if a value exists and it contains profanity.
        if (value && checkText(language, value)) {
            return Promise.reject(new Error(message)); // Use new Error() for a more robust promise rejection.
        }
        return Promise.resolve();
    };
};
